import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { useEnvs } from '@next-box/envs';
import { useEffect, useState } from 'react';
import { FeaturePlayer } from '#components/FeaturePlayer/index.tsx';
import { EpisodesDrawer } from '#components/FeaturePlayerModal/Tv/EpisodesDrawer.tsx';
import { formatEpisodes } from '#components/FeaturePlayerModal/Tv/helpers/formatEpisodes.ts';
import { getNextEpisode, getPrevEpisode } from '#components/FeaturePlayerModal/Tv/helpers/getNextPrevEpisode.ts';
import { type TvSeasonEpisodeData } from '#components/FeaturePlayerModal/Tv/types.ts';
import { PlayerNavigation } from '#components/PlayerSelectorTabs/FeaturePlayerTabPanel/PlayerNavigation.tsx';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { useStore } from '#store/useStore.ts';
import { type PathIds } from '#types.js';

const GET_EVERY_EPISODE = "\nquery GetEveryEpisode(\n  $id: ID!\n) {\n  tv(id: $id) {\n    seasons {\n      name\n      episodeCount\n      seasonNumber\n      episodes {\n        airDate\n        episodeNumber\n        name\n        runtime\n        stillPath\n      }\n    }\n  }\n}\n\nfragment SeasonEpisodes on Season {\n  episodes {\n    episodeNumber\n    name\n    stillPath\n    voteAverage\n    voteCount\n  }\n}\n";

export type FeaturePlayerTvTabPanelProps = {
  initialPathIds: PathIds;
  title: string;
};

export const FeaturePlayerTvTabPanel = ({ initialPathIds, title }: FeaturePlayerTvTabPanelProps) => {
  const { config } = useToolbelt();
  const { getEnv } = useEnvs();
  const addRecentlyWatchedTv = useStore(store => store.addRecentlyWatchedTv);
  const updateContinueWatching = useStore(store => store.updateContinueWatching);
  const getContinueWatchingTvEntry = useStore(store => store.getContinueWatchingTvEntry);
  // @ts-expect-error TODO: Need to refactor so tvId is string
  const entry = getContinueWatchingTvEntry(initialPathIds[0]);
  const [episodesDrawOpen, setEpisodesDrawOpen] = useState(false);
  const [pathIds, setPathIds] = useState<PathIds>(entry ?? initialPathIds);
  const [tvId, seasonNumber, episodeNumber] = pathIds;
  const seasonNumberWithDefault = seasonNumber ?? 1;
  const episodeNumberWithDefault = episodeNumber ?? 1;
  const baseUrl = getEnv('NEXT_PUBLIC_EXOTIC_PINK_PTARMIGAN_PROVIDER') ?? '';

  const { execute, loading, result } = useQuery<TvSeasonEpisodeData>(GET_EVERY_EPISODE, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id: tvId } }, { data: { initiator: 'FeaturePlayerTvModal' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tvId]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const items = formatEpisodes(result?.data, config);
  const hasEpisodes = !loading && items.length > 0;
  const prevEpisode = getPrevEpisode(items, [seasonNumberWithDefault, episodeNumberWithDefault]);
  const nextEpisode = getNextEpisode(items, [seasonNumberWithDefault, episodeNumberWithDefault]);

  return (
    <>
      <FeaturePlayer
        height="40%"
        onClick={() => {
          if (nextEpisode) {
            updateContinueWatching(pathIds);
          } else {
            // @ts-expect-error TODO: Need to refactor so tvId is string
            addRecentlyWatchedTv(tvId);
          }
        }}
        responsiveHeights={{
          md: `40%`,
          sm: '56.25%',
          xs: '75%',
        }}
        url={`${atob(baseUrl)}/tv/${String(tvId)}/${String(seasonNumberWithDefault)}/${String(episodeNumberWithDefault)}`}
      />
      {hasEpisodes ? (
        <PlayerNavigation
          hasNext={!!nextEpisode}
          hasPrev={!!prevEpisode}
          onNext={() => {
            if (nextEpisode) {
              setPathIds([tvId, ...nextEpisode]);
            }
          }}
          onOpenPlaylist={() => {
            setEpisodesDrawOpen(true);
          }}
          onPrev={() => {
            if (prevEpisode) {
              setPathIds([tvId, ...prevEpisode]);
            }
          }}
          playlist={hasEpisodes}
        >
          {`${title} - S${String(seasonNumberWithDefault)}E${String(episodeNumberWithDefault)}`}
        </PlayerNavigation>
      ) : undefined}
      {hasEpisodes ? (
        <EpisodesDrawer
          items={items}
          onEpisodeClick={(seNo, epNo) => {
            setPathIds([tvId, seNo, epNo]);
            setEpisodesDrawOpen(false);
          }}
          open={episodesDrawOpen}
          selectedEpisodeNumber={episodeNumberWithDefault}
          selectedSeasonNumber={seasonNumberWithDefault}
          setOpen={setEpisodesDrawOpen}
        />
      ) : undefined}
    </>
  );
};
